import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {Renderer} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {PaletteColor} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {ThemeContextType} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface ActivityPillProps {
  color?: PaletteColorsEnum;
  isPrivate?: boolean;
  hasHover?: boolean;
  renderIcon?: Renderer;
  renderTooltip?: Renderer;
}

/*
 * Style.
 */

interface OuterStyleProps {
  $color?: PaletteColorsEnum;
  $hasHover?: boolean;
}
const StyledOuterDiv = styled.div<OuterStyleProps>`
  height: 16px;
  border: 1px solid;
  border-radius: 2px;

  ${(p) => buildDynamicStyle(p)};
`;

const StyledInnerDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 0 1px;
  height: 16px;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
`;

function buildDynamicStyle(props: OuterStyleProps & ThemeContextType) {
  // If no color was provided, fallback to grey.
  const {$color, $hasHover, theme} = props;
  const colors = theme.palette[$color || PaletteColorsEnum.GREY];

  // Permanent hover state.
  if ($hasHover) {
    return buildHoverColorStyle(colors);
  }

  // Normal hover behavior.
  return css`
    ${buildColorStyle(colors)};
    &:hover {
      ${buildHoverColorStyle(colors)};
    }
  `;
}

function buildColorStyle(colors: PaletteColor) {
  return css`
    color: ${colors.shade40};
    background-color: ${colors.shade10};
    border-color: ${colors.shade40};
  `;
}

function buildHoverColorStyle(colors: PaletteColor) {
  return css`
    color: ${colors.shade40};
    background-color: ${colors.shade20};
    border-color: ${colors.shade50};
  `;
}

const StyledContentDiv = styled.div`
  padding: 0 2px;
  overflow: hidden;

  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryTiny};
  letter-spacing: 0.17px;
  text-transform: uppercase;
  line-height: 14px;
`;

/*
 * Component.
 */

export const ActivityPill: FC<PropsWithChildren<ActivityPillProps>> = (props) => {
  const {renderIcon, isPrivate} = props;
  return (
    <StyledOuterDiv $color={props.color} $hasHover={props.hasHover}>
      <TooltipCoordinator render={props.renderTooltip}>
        <StyledInnerDiv>
          {renderIcon && renderLeftIcon(renderIcon)}
          <StyledContentDiv>{props.children}</StyledContentDiv>
          {isPrivate && renderLockIcon()}
        </StyledInnerDiv>
      </TooltipCoordinator>
    </StyledOuterDiv>
  );
};

function renderLeftIcon(renderIcon: Renderer) {
  return <StyledIconDiv>{renderIcon()}</StyledIconDiv>;
}

function renderLockIcon() {
  return (
    <StyledIconDiv>
      <Icon name="lockFilledTiny" />
    </StyledIconDiv>
  );
}
